import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X, Instagram, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg, igUrl } from "@/lib/api";
import { RoleBadge } from "@/components/RoleBadge";

const Login = ({ onDone }) => {
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/login", { password: pw }); onDone(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="mx-auto mt-20 max-w-sm space-y-4 rounded-[2rem] border border-[#EAE4DC] bg-white p-8">
      <h1 className="font-serif text-3xl">Admin</h1>
      <input data-testid="admin-password-input" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" className="h-11 w-full rounded-xl border px-4 text-sm outline-none focus:border-pink-400" />
      <button data-testid="admin-login-button" className="w-full rounded-full bg-stone-900 py-3 text-sm text-white">Log in</button>
    </form>
  );
};

const ClaimRow = ({ c, onAct }) => (
  <div data-testid="admin-claim-row" className="flex flex-wrap items-center gap-4 border-b border-[#EAE4DC] py-5">
    <div className="min-w-0 flex-1">
      <a href={igUrl(c.username)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono hover:text-pink-600"><Instagram className="h-4 w-4" />@{c.username}</a>
      <p className="mt-1 flex items-center gap-2 text-xs text-stone-500"><RoleBadge role={c.role} />{c.is_returning && "returning · "}{formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}</p>
    </div>
    <span data-testid="admin-claim-code" className="rounded-lg bg-stone-100 px-3 py-1.5 font-mono text-sm tracking-widest">{c.code}</span>
    <button data-testid={`admin-approve-${c.code}`} onClick={() => onAct(c, "approve")} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-4 py-2 text-xs text-white"><Check className="h-3.5 w-3.5" />Approve</button>
    <button data-testid={`admin-reject-${c.code}`} onClick={() => onAct(c, "reject")} className="inline-flex items-center gap-1 rounded-full border px-4 py-2 text-xs"><X className="h-3.5 w-3.5" />Reject</button>
  </div>
);

const Panel = ({ onLogout }) => {
  const qc = useQueryClient();
  const { data: claims = [] } = useQuery({ queryKey: ["admin-claims"], queryFn: () => api.get("/admin/claims").then((r) => r.data), refetchInterval: 10000 });
  const { data: users = [] } = useQuery({ queryKey: ["admin-users"], queryFn: () => api.get("/admin/users").then((r) => r.data) });
  const reload = () => { qc.invalidateQueries({ queryKey: ["admin-claims"] }); qc.invalidateQueries({ queryKey: ["admin-users"] }); };
  const act = async (c, action) => {
    try { await api.post(`/admin/claims/${c.id}/${action}`); toast.success(action === "approve" ? `@${c.username} verified` : "Request rejected"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove @${u.username}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Removed"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
      <div className="flex items-end justify-between">
        <h1 className="font-serif text-4xl">Verification requests</h1>
        <button data-testid="admin-logout" onClick={onLogout} className="text-sm text-stone-500 underline">Log out</button>
      </div>
      <p className="mt-3 text-sm text-stone-600">Open your Instagram DMs. Approve a request only if the code arrived <b>from that exact handle</b>.</p>
      <Tabs defaultValue="pending" className="mt-8">
        <TabsList><TabsTrigger value="pending" data-testid="admin-tab-pending">Pending ({claims.length})</TabsTrigger><TabsTrigger value="users" data-testid="admin-tab-users">Verified ({users.length})</TabsTrigger></TabsList>
        <TabsContent value="pending">
          {claims.map((c) => <ClaimRow key={c.id} c={c} onAct={act} />)}
          {!claims.length && <p data-testid="admin-no-claims" className="py-16 text-center text-stone-500">No pending requests.</p>}
        </TabsContent>
        <TabsContent value="users">
          {users.map((u) => (
            <div key={u.id} data-testid="admin-user-row" className="flex items-center gap-3 border-b border-[#EAE4DC] py-4">
              <span className="font-mono">@{u.username}</span><RoleBadge role={u.role} />
              <button data-testid={`admin-remove-${u.username}`} onClick={() => remove(u)} className="ml-auto text-stone-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default function Admin() {
  const qc = useQueryClient();
  const { data: ok, isLoading, refetch } = useQuery({ queryKey: ["admin-me"], queryFn: () => api.get("/admin/me").then(() => true).catch(() => false) });
  if (isLoading) return <div className="min-h-[60vh]" />;
  if (!ok) return <Login onDone={refetch} />;
  return <Panel onLogout={async () => { await api.post("/admin/logout"); qc.clear(); refetch(); }} />;
}
